import { ColorBarPatch, VdpLayout } from '../types';

export const STANDARD_CMYK_PATCHES: ColorBarPatch[] = [
  { id: 'c', name: 'C', color: '#00ffff', cmyk: { c: 1, m: 0, y: 0, k: 0 } },
  { id: 'm', name: 'M', color: '#ff00ff', cmyk: { c: 0, m: 1, y: 0, k: 0 } },
  { id: 'y', name: 'Y', color: '#ffff00', cmyk: { c: 0, m: 0, y: 1, k: 0 } },
  { id: 'k', name: 'K', color: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 1 } },
];

export const CMYK_TINTS_PATCHES: ColorBarPatch[] = [
  { id: 'c100', name: 'C 100%', color: '#00ffff', cmyk: { c: 1, m: 0, y: 0, k: 0 } },
  { id: 'm100', name: 'M 100%', color: '#ff00ff', cmyk: { c: 0, m: 1, y: 0, k: 0 } },
  { id: 'y100', name: 'Y 100%', color: '#ffff00', cmyk: { c: 0, m: 0, y: 1, k: 0 } },
  { id: 'k100', name: 'K 100%', color: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 1 } },
  { id: 'k75', name: 'K 75%', color: '#404040', cmyk: { c: 0, m: 0, y: 0, k: 0.75 } },
  { id: 'k50', name: 'K 50%', color: '#808080', cmyk: { c: 0, m: 0, y: 0, k: 0.5 } },
  { id: 'k25', name: 'K 25%', color: '#bfbfbf', cmyk: { c: 0, m: 0, y: 0, k: 0.25 } },
];

export const CMYK_RGB_GRAY_PATCHES: ColorBarPatch[] = [
  { id: 'c', name: 'Cyan', color: '#00ffff', cmyk: { c: 1, m: 0, y: 0, k: 0 } },
  { id: 'm', name: 'Magenta', color: '#ff00ff', cmyk: { c: 0, m: 1, y: 0, k: 0 } },
  { id: 'y', name: 'Yellow', color: '#ffff00', cmyk: { c: 0, m: 0, y: 1, k: 0 } },
  { id: 'k', name: 'Black', color: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 1 } },
  { id: 'gray50', name: '50% Gray', color: '#808080', cmyk: { c: 0, m: 0, y: 0, k: 0.5 } },
  { id: 'red', name: 'Red', color: '#ff0000', cmyk: { c: 0, m: 1, y: 1, k: 0 } },
  { id: 'green', name: 'Green', color: '#00ff00', cmyk: { c: 1, m: 0, y: 1, k: 0 } },
  { id: 'blue', name: 'Blue', color: '#0000ff', cmyk: { c: 1, m: 1, y: 0, k: 0 } },
];

export const HEXACHROME_PATCHES: ColorBarPatch[] = [
  { id: 'c', name: 'Cyan', color: '#00ffff', cmyk: { c: 1, m: 0, y: 0, k: 0 } },
  { id: 'm', name: 'Magenta', color: '#ff00ff', cmyk: { c: 0, m: 1, y: 0, k: 0 } },
  { id: 'y', name: 'Yellow', color: '#ffff00', cmyk: { c: 0, m: 0, y: 1, k: 0 } },
  { id: 'k', name: 'Black', color: '#000000', cmyk: { c: 0, m: 0, y: 0, k: 1 } },
  { id: 'orange', name: 'Orange', color: '#ff6600', cmyk: { c: 0, m: 0.6, y: 1, k: 0 } },
  { id: 'green', name: 'Green', color: '#00aa44', cmyk: { c: 1, m: 0, y: 0.6, k: 0.3 } },
];

export const SPOT_BRAND_SAMPLE_PATCHES: ColorBarPatch[] = [
  { id: 'c', name: 'C', color: '#00ffff' },
  { id: 'm', name: 'M', color: '#ff00ff' },
  { id: 'y', name: 'Y', color: '#ffff00' },
  { id: 'k', name: 'K', color: '#000000' },
  { id: 'pantone_red', name: 'Pantone 032 C', color: '#ef3340' },
  { id: 'gold', name: 'Special Gold', color: '#d4af37' },
];

/**
 * Returns the effective color patches for the given layout configuration
 */
export function getActiveColorBarPatches(layout?: Partial<VdpLayout>): ColorBarPatch[] {
  if (layout?.colorBarsPatches && layout.colorBarsPatches.length > 0) {
    return layout.colorBarsPatches;
  }
  const preset = layout?.colorBarsPreset;
  if (preset === 'cmyk_tints') return CMYK_TINTS_PATCHES;
  if (preset === 'cmyk_rgb') return CMYK_RGB_GRAY_PATCHES;
  if (preset === 'hexachrome') return HEXACHROME_PATCHES;
  if (preset === 'spot_custom') return SPOT_BRAND_SAMPLE_PATCHES;
  return STANDARD_CMYK_PATCHES;
}

/**
 * Parse a hex string to RGB (0..1)
 */
export function hexToRgbRatio(hex: string): { r: number; g: number; b: number } {
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = clean.split('').map(c => c + c).join('');
  }
  if (clean.length !== 6) {
    return { r: 0, g: 0, b: 0 };
  }
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  return {
    r: isNaN(r) ? 0 : r,
    g: isNaN(g) ? 0 : g,
    b: isNaN(b) ? 0 : b
  };
}

/**
 * Convert RGB to simple CMYK estimation
 */
export function rgbToCmykRatio(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
  const k = 1 - Math.max(r, g, b);
  if (k === 1) {
    return { c: 0, m: 0, y: 0, k: 1 };
  }
  const c = (1 - r - k) / (1 - k);
  const m = (1 - g - k) / (1 - k);
  const y = (1 - b - k) / (1 - k);
  return {
    c: Math.max(0, Math.min(1, c)),
    m: Math.max(0, Math.min(1, m)),
    y: Math.max(0, Math.min(1, y)),
    k: Math.max(0, Math.min(1, k)),
  };
}
